import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminShell } from "@/components/admin/AdminShell";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { SuppliersSummaryCards } from "@/pages/admin/suppliers/SuppliersSummaryCards";
import { SuppliersFiltersBar } from "@/pages/admin/suppliers/SuppliersFiltersBar";
import { SuppliersTable } from "@/pages/admin/suppliers/SuppliersTable";
import { SupplierFormDialog } from "@/pages/admin/suppliers/SupplierFormDialog";
import { SupplierLedgerDialog } from "@/pages/admin/suppliers/SupplierLedgerDialog";
import { AdjustmentDialog } from "@/pages/admin/suppliers/AdjustmentDialog";
import { DeleteSupplierDialog } from "@/pages/admin/suppliers/DeleteSupplierDialog";
import { EMPTY_ADDR, EMPTY_FORM } from "@/pages/admin/suppliers/supplierHelpers";
import { hasAdminSession } from "@/lib/session";

export default function Suppliers() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [dualMap, setDualMap] = useState({});
  const [ledgerTotals, setLedgerTotals] = useState({ total_gold_fine_g: 0, total_inr: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selected, setSelected] = useState(null);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({ amount: "", note: "", kind: "inr" });
  const [staff, setStaff] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [goldRate, setGoldRate] = useState(0);
  const [convDir, setConvDir] = useState(null);
  const [convAmount, setConvAmount] = useState("");
  const [converting, setConverting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = async (status = statusFilter) => {
    const { data } = await api.get("/purchase/suppliers", { params: status !== "all" ? { status } : {} });
    setSuppliers(data);
  };
  const loadDualSummary = async () => {
    try {
      const { data } = await api.get("/purchase/suppliers/dual-ledger-summary");
      const map = {};
      (data.suppliers || []).forEach((s) => { map[s.supplier_id] = s; });
      setDualMap(map);
      setLedgerTotals({ total_gold_fine_g: data.total_gold_fine_g, total_inr: data.total_inr });
    } catch { /* non-critical — UI still shows base supplier list without dual-ledger totals */ }
  };
  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    load("all"); loadDualSummary();
    api.get("/crm/me").then(({ data }) => setStaff(data)).catch(() => {});
    api.get("/rates/public").then(({ data }) => setGoldRate(data.gold_rate_per_gram_24k || 0)).catch(() => {});
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(statusFilter); /* eslint-disable-next-line */ }, [statusFilter]);

  const filtered = suppliers.filter((s) => !q || (s.name + s.company_name + s.phone).toLowerCase().includes(q.toLowerCase()));

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY_FORM, ...s, address: { ...EMPTY_ADDR, ...(s.address || {}) } }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Supplier name is required"); return; }
    try {
      if (editing) { await api.put(`/purchase/suppliers/${editing.id}`, form); toast.success("Supplier updated"); }
      else { await api.post("/purchase/suppliers", form); toast.success("Supplier added"); }
      setDialogOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const toggleStatus = async (s) => {
    try { await api.put(`/purchase/suppliers/${s.id}/status`); toast.success(`${s.name} ${s.status === "active" ? "deactivated" : "activated"}`); load(); }
    catch { toast.error("Failed to update status"); }
  };

  const openLedger = async (s) => {
    setSelected(s); setLedgerOpen(true); setLedger(null); setLedgerLoading(true); setConvDir(null); setConvAmount("");
    try { const { data } = await api.get(`/purchase/suppliers/${s.id}/dual-ledger`); setLedger(data); }
    catch { toast.error("Failed to load ledger"); setLedger({ gold_entries: [], inr_entries: [], gold_balance_fine_g: 0, inr_balance: 0 }); }
    finally { setLedgerLoading(false); }
  };
  const downloadLedger = async (s) => {
    setDownloading(true);
    try {
      const res = await api.get(`/purchase/suppliers/${s.id}/dual-ledger/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Supplier-Metal-Ledger-${(s.name || "Supplier").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download statement"); }
    finally { setDownloading(false); }
  };

  const startConvert = (dir) => {
    setConvDir(dir);
    if (dir === "rupee_to_gold") setConvAmount(ledger?.inr_balance > 0 ? String(Math.round(ledger.inr_balance)) : "");
    else setConvAmount(ledger?.gold_balance_fine_g > 0 ? String(ledger.gold_balance_fine_g) : "");
  };
  const convPreview = (() => {
    const amt = Number(convAmount) || 0;
    if (!amt || !goldRate) return null;
    return convDir === "rupee_to_gold" ? `${(amt / goldRate).toFixed(3)} g` : formatINR(amt * goldRate);
  })();
  const submitConvert = async () => {
    const amt = Number(convAmount) || 0;
    if (amt <= 0) { toast.error("Enter an amount to convert"); return; }
    setConverting(true);
    try {
      const { data } = await api.post(`/purchase/suppliers/${selected.id}/convert`, { direction: convDir, amount: amt, rate_per_gram_24k: goldRate });
      setLedger(data.ledger); setConvDir(null); setConvAmount("");
      toast.success(`Converted at ${formatINR(data.rate_per_gram_24k)}/g`);
      loadDualSummary(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Conversion failed"); }
    finally { setConverting(false); }
  };

  const saveAdjustment = async () => {
    if (!adjForm.amount) { toast.error("Enter an amount"); return; }
    try {
      await api.post(`/purchase/suppliers/${selected.id}/adjustments`, { amount: Number(adjForm.amount), note: adjForm.note, kind: adjForm.kind });
      toast.success("Adjustment recorded"); setAdjOpen(false); setAdjForm({ amount: "", note: "", kind: "inr" });
      openLedger(selected); load(); loadDualSummary();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add adjustment"); }
  };

  const doDelete = async () => {
    if (!deleteFor) return;
    try { await api.delete(`/purchase/suppliers/${deleteFor.id}`); toast.success("Supplier deleted"); setDeleteFor(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not delete supplier"); }
  };

  return (
    <AdminShell title="Suppliers">
      <SuppliersSummaryCards ledgerTotals={ledgerTotals} />

      <SuppliersFiltersBar statusFilter={statusFilter} setStatusFilter={setStatusFilter} q={q} setQ={setQ} onAddSupplier={openCreate} />

      <SuppliersTable suppliers={filtered} dualMap={dualMap} staff={staff} onOpenLedger={openLedger} onEdit={openEdit} onToggleStatus={toggleStatus} onDelete={setDeleteFor} />

      <SupplierFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editing={editing} form={form} setForm={setForm} onSave={save} />

      <SupplierLedgerDialog
        open={ledgerOpen} onClose={() => setLedgerOpen(false)} supplier={selected} ledger={ledger} ledgerLoading={ledgerLoading}
        goldRate={goldRate} downloading={downloading} onDownload={downloadLedger}
        convDir={convDir} convAmount={convAmount} setConvAmount={setConvAmount} convPreview={convPreview} converting={converting}
        onStartConvert={startConvert} onCancelConvert={() => { setConvDir(null); setConvAmount(""); }} onSubmitConvert={submitConvert}
        onOpenAdjustment={() => setAdjOpen(true)}
      />

      <AdjustmentDialog open={adjOpen} onClose={() => setAdjOpen(false)} adjForm={adjForm} setAdjForm={setAdjForm} onSave={saveAdjustment} />

      <DeleteSupplierDialog deleteFor={deleteFor} onClose={() => setDeleteFor(null)} onConfirm={doDelete} />
    </AdminShell>
  );
}
